import { WarningOutlined } from '@ant-design/icons';
import { ChannelSelect } from '@features/SendoutCreationModal/components/SendMessageModal/components/ChannelSelect';
import { FrequencySelect } from '@features/SendoutCreationModal/components/SendMessageModal/components/FrequencySelect';
import { IntegrationSelect } from '@features/SendoutCreationModal/components/SendMessageModal/components/IntegrationSelect';
import { TagSelect } from '@features/SendoutCreationModal/components/SendMessageModal/components/TagSelect';
import { useCreateSendout } from '@features/SendoutCreationModal/hooks/useCreateSendout';
import { usePrepareSendout } from '@features/SendoutCreationModal/hooks/usePrepareSendout';
import { Checkbox, Form, Modal, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { useLazyGetBranchQuery } from '@app/api/Branches';
import { timezonesApi } from '@app/api/Timezones';
import type { ModelTimezone } from '@app/api/Timezones';
import { MessageInputWithFilesUpload } from '@app/components/MessageInputWithFilesUpload';
import { DEFAULT_COMPONENT_MESSAGE } from '@app/components/MessageInputWithFilesUpload/data/defaultComponentMessage';
import { Schedule, useSchedule } from '@app/components/Schedule';
import { PREFIX } from '@app/constants';
import type { NSendMessageModal } from '@app/features/SendoutCreationModal/components/SendMessageModal/SendMessageModal.types';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useAppDispatch } from '@app/store';

import type { CheckboxChangeEvent } from 'antd/es/checkbox';

import '@app/features/SendoutCreationModal/components/SendMessageModal/styles/SendMessageModal.desktop.scss';

dayjs.extend(customParseFormat);
const { Text } = Typography;
const { useForm } = Form;

export const SendMessageModal =
  forwardRef<NSendMessageModal.SendMessageModalApi>((_, ref) => {
    const { showNotification } = useNotification();
    const dispatch = useAppDispatch();
    const { activeBranchId } = useUserPrint();
    const { initialScheduleValues } = useSchedule();

    const [timezones, setTimezones] = useState<ModelTimezone[]>([]);
    const [isOpen, toggleVisibility] = useState(false);
    const [state, setState] = useState<NSendMessageModal.SendMessageModalState>(
      {
        actionType: 'all',
        usage: 'clients',
        activeBranchId: 0,
        selectedIds: [],
        searchId: '',
      }
    );
    const [onSuccessCb, setOnSuccessCb] = useState<() => void>();
    const [channel, setChannel] =
      useState<NSendMessageModal.SendMessageFormValues['channel']>();

    const [form] = useForm<NSendMessageModal.SendMessageFormValues>();
    const { submit } = form;

    const { prepareSendoutCreation } = usePrepareSendout(state?.activeBranchId);

    const { createSendoutHandler, isCreateSendoutLoading } = useCreateSendout({
      ...state,
    });
    const [disabled, setDisabled] = useState(() => isCreateSendoutLoading);

    const [showSchedule, setShowSchedule] = useState(
      localStorage.getItem('disabledShowSchedule') !== 'true'
    );

    const [getBranch] = useLazyGetBranchQuery();

    const getTimezonesHandler = useCallback(async () => {
      const request = dispatch(
        timezonesApi.endpoints.GetTimezones.initiate(undefined, {
          forceRefetch: true,
        })
      );

      try {
        return await request.unwrap();
      } catch (error) {
        showNotification(error);
        return Promise.reject(error);
      } finally {
        request.unsubscribe?.();
      }
    }, [dispatch, showNotification]);

    const onSubmit = useCallback(
      async (values: NSendMessageModal.SendMessageFormValues) => {
        try {
          const formattedValues = {
            ...values,
            channel: values.channel === 'defaultChannel' ? '' : values.channel,
          };
          await createSendoutHandler(formattedValues);
          toggleVisibility(false);
          onSuccessCb?.();
        } catch {
        } finally {
          form.resetFields();
        }
      },
      [createSendoutHandler, form, onSuccessCb]
    );

    const handleCancel = useCallback(() => {
      form.resetFields();
      setChannel(undefined);
      toggleVisibility(false);
    }, [form]);

    const getBranchHandler = useCallback(async () => {
      if (!activeBranchId) return;

      try {
        const response = await getBranch({
          ActiveBranchId: activeBranchId,
          branchId: activeBranchId,
        }).unwrap();

        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    }, [activeBranchId, getBranch]);

    const handleScheduleChange = (e: CheckboxChangeEvent) => {
      setShowSchedule(e.target.checked);
      localStorage.setItem('disabledShowSchedule', String(!e.target.checked));
    };

    useImperativeHandle(ref, () => ({
      open: (data, onSuccess) => {
        toggleVisibility(true);
        setState({ ...data });
        onSuccess && setOnSuccessCb(() => onSuccess);
      },
    }));

    const initTimezones = useCallback(async () => {
      try {
        const [timezonesResponse, branchResponse] = await Promise.allSettled([
          getTimezonesHandler(),
          getBranchHandler(),
        ]);
        let timezones: ModelTimezone[] = [];
        let timezone: string = '';

        if (timezonesResponse.status === 'fulfilled') {
          timezones = timezonesResponse.value;
          setTimezones(timezones);
        }

        if (
          branchResponse &&
          branchResponse.status === 'fulfilled' &&
          branchResponse.value
        ) {
          const branch = branchResponse.value;
          timezone = branch.Timezone ?? '';
        }

        if (timezone) {
          const formattedTimezone = timezone
            .replace('Standard Time', '')
            .trim();
          const currentTimezone = timezones.find(({ Id }) => {
            if (!Id) return false;
            const exactEquality = Id === timezone;
            const formattedEquality = Id.includes(formattedTimezone);

            return exactEquality || formattedEquality;
          });

          if (currentTimezone) {
            form.setFieldsValue({
              timezone: currentTimezone.Id ?? '',
            });
          }
        }
      } catch (error) {
        showNotification(error);
      }
    }, [getTimezonesHandler, getBranchHandler, showNotification, form]);

    useEffect(() => {
      if (isOpen) {
        initTimezones();
      }
    }, [initTimezones, isOpen]);

    return (
      <Modal
        className={`${PREFIX}send-message-modal`}
        centered
        open={isOpen}
        onOk={submit}
        okText={'Отправить'}
        onCancel={handleCancel}
        title="Отправить сообщение"
        okButtonProps={{ loading: disabled, disabled }}
        cancelButtonProps={{ disabled }}
        destroyOnHidden
        afterClose={() => {
          form.resetFields();
          setChannel(undefined);
          setDisabled(false);
        }}
      >
        <Form
          form={form}
          onFinish={onSubmit}
          onValuesChange={(changedValues) => {
            if ('channel' in changedValues) {
              setChannel(changedValues.channel);
            }
          }}
          initialValues={{
            message: DEFAULT_COMPONENT_MESSAGE,
            tags: [],
            frequency: 3600,
            files: [],
            schedule: initialScheduleValues,
            useSchedule:
              localStorage.getItem('disabledShowSchedule') === 'true'
                ? false
                : true,
          }}
          clearOnDestroy
        >
          <MessageInputWithFilesUpload
            formInstance={form}
            selectedClientIds={state.selectedIds}
            onDisabledChange={(isDisabled) =>
              setDisabled(isDisabled || isCreateSendoutLoading)
            }
            socialType={channel}
            onChange={({ target: { value } }) => {
              form.setFieldsValue({ message: value });
            }}
          />

          <ChannelSelect prepareSendoutCreation={prepareSendoutCreation} />
          <IntegrationSelect
            prepareSendoutCreation={prepareSendoutCreation}
            channel={channel || ''}
          />
          <TagSelect prepareSendoutCreation={prepareSendoutCreation} />
          <FrequencySelect />
          <Form.Item>
            <Text strong type="danger">
              <WarningOutlined /> Внимание! {'  '}
            </Text>
            <Text type="danger">
              Если аккаунт интеграции новый, рекомендуем увеличить периодичность
              отправляемых сообщений
            </Text>
          </Form.Item>
          <Form.Item name="useSchedule" valuePropName="checked">
            <Checkbox onChange={handleScheduleChange}>
              Отправлять сообщения по графику
            </Checkbox>
          </Form.Item>

          {showSchedule && (
            <>
              <Form.Item>
                <Text strong>График рассылки:</Text>
                <Schedule form={form} />
              </Form.Item>
              <Form.Item
                name="timezone"
                label="Часовой пояс"
                colon
                wrapperCol={{ offset: 2, span: 32 }}
                required
                rules={[{ required: true, message: 'Выберите часовой пояс' }]}
              >
                <Select
                  placeholder="Выберите часовой пояс"
                  optionLabelProp="label"
                  options={timezones.map(({ Name, Id }) => ({
                    label: Name,
                    value: Id,
                  }))}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    );
  });
